import React from 'react';

import { TeamNanoID } from '../../../../../../teams/teamsTypes';

import {
  AddFundsModalButtonSteps,
  AddFundsModalButtonData,
  AddFundsModalButtonWireBillingInfoStepAction,
  AddFundsModalButtonPaymentMethodAndAmountStepAction,
  AddFundsModalButtonStepBackAction,
  AddFundsModalButtonCloseAction
} from '../../AddFundsModalButton.types';

import {
  FetchTeamAddFundsQueryResponse,
  FETCH_TEAM_ADD_FUNDS_QUERY
} from '../../../../../../teams/queries/fetchTeamAddFunds.query';

import { useCompanyBalanceAccounts } from '../../../../../../accounts/hooks/useCompanyBalanceAccounts';
import { useCacheTeam } from '../../../../../../teams/hooks/useCacheTeam';

import { AddFundsModalButtonPaymentMethodAndAmountStep } from '../AddFundsModalButtonPaymentMethodAndAmountStep';
import { AddFundsModalButtonWireBillingInfoStep } from '../AddFundsModalButtonWireBillingInfoStep';
import { AddFundsModalButtonCardBillingInfoAndCardStep } from '../AddFundsModalButtonCardBillingInfoAndCardStep';
import { AddFundsModalButtonWireInvoiceInfoStep } from '../AddFundsModalButtonWireInvoiceInfoStep';

import { AlertMessage } from '../../../../../../../helpers/AlertMessage';
import { LoadingSkeleton } from '../../../../../../../helpers/LoadingSkeleton';

import { TeamCache } from '../../../../../../teams/TeamCache';

interface AddFundsModalButtonContentProps {
  companyNanoId: TeamNanoID;
  addFundsData: AddFundsModalButtonData;
  addFundsStep: AddFundsModalButtonSteps;
  onPaymentMethodAndAmountStep: AddFundsModalButtonPaymentMethodAndAmountStepAction;
  onWireBillingInfoStep: AddFundsModalButtonWireBillingInfoStepAction;
  onStepBack: AddFundsModalButtonStepBackAction;

  onClose: AddFundsModalButtonCloseAction;
}

function AddFundsModalButtonContent({
  companyNanoId,
  addFundsData,
  addFundsStep,
  onPaymentMethodAndAmountStep,
  onWireBillingInfoStep,
  onStepBack,
  onClose
}: AddFundsModalButtonContentProps) {
  const {
    companyBalance,
    companyBalanceAccountsErrorMessage,
    companyBalanceAccountsFetched
  } = useCompanyBalanceAccounts({ companyNanoId });

  const { team, teamError, teamFetched } =
    useCacheTeam<FetchTeamAddFundsQueryResponse>({
      cacheKey: TeamCache.showCompanyAddFundsCacheKey(),
      query: FETCH_TEAM_ADD_FUNDS_QUERY,
      uuid: companyNanoId
    });

  return (
    <LoadingSkeleton loaded={companyBalanceAccountsFetched && teamFetched}>
      <AlertMessage message={companyBalanceAccountsErrorMessage || teamError} />
      {addFundsStep === AddFundsModalButtonSteps.PAYMENT_METHOD_AND_AMOUNT ? (
        <AddFundsModalButtonPaymentMethodAndAmountStep
          companyCashBalance={companyBalance}
          companyPreferredPaymentMethod={team?.preferredPaymentMethod}
          addFundsData={addFundsData}
          onPaymentMethodAndAmountStep={onPaymentMethodAndAmountStep}
          onClose={onClose}
        />
      ) : null}
      {addFundsStep === AddFundsModalButtonSteps.WIRE_BILLING_INFO ? (
        <AddFundsModalButtonWireBillingInfoStep
          companyCashBalance={companyBalance}
          companyNanoId={companyNanoId}
          companyDeferredPayment={team?.deferredPayment}
          companyTerms={team?.terms}
          addFundsData={addFundsData}
          onStepBack={onStepBack}
          onWireBillingInfoStep={onWireBillingInfoStep}
        />
      ) : null}
      {addFundsStep === AddFundsModalButtonSteps.CARD_BILLING_INFO_AND_CARD ? (
        <AddFundsModalButtonCardBillingInfoAndCardStep
          companyCashBalance={companyBalance}
          companyNanoId={companyNanoId}
          companyDeferredPayment={team?.deferredPayment}
          companyTerms={team?.terms}
          addFundsData={addFundsData}
          onStepBack={onStepBack}
        />
      ) : null}
      {addFundsStep === AddFundsModalButtonSteps.WIRE_INVOICE_INFO ? (
        <AddFundsModalButtonWireInvoiceInfoStep
          addFundsData={addFundsData}
          onClose={onClose}
        />
      ) : null}
    </LoadingSkeleton>
  );
}

export default AddFundsModalButtonContent;
